import { useState } from "react";
import { makeStyles } from "tss-react/mui";
import { MiCard, MiContentDrawer } from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  ICONS_WEB,
} from "@miplatform/constants";
import * as mdi from "@mdi/js";
import { Box } from "@mui/system";
import { AddHomeForm } from "../AddHomeForm/AddHomeForm";
import { useAddHomeForm } from "../AddHomeForm/useAddHomeForm";
import { HomeAndAccountSearch } from "../HomeAndAccountSearch/HomeAndAccountSearch";

/**
 *
 * @param {Object} Props
 * @param {boolean} Props.open - Whether the drawer is open.
 * @param {Object} [Props.account] - The account to which the home will be added.
 * @param {string} [Props.builderId] - Optional builder ID to prefill in the form.
 * @param {string} [Props.communityId] - Optional community ID to prefill in the form.
 * @param {string} [Props.city] - Optional city to prefill in the form.
 * @param {string} [Props.regionCode] - Optional region code to prefill in the form.
 * @param {Array<string>} [Props.readOnlyFields] - Fields that should be read-only in the form.
 * @param {Function} Props.onClose - Function to call when the drawer is closed.
 * @param {Function} Props.onCreate - Function to call after a home is successfully created.
 * @param {string} [Props.title] - The title of the drawer.
 * @param {boolean} [Props.showHomeSearch] - Whether to show the home search component.
 * @param {Function} [Props.onCreateWithSelectedHome] - Function to call after a home is created with selected home data.
 * @param {Object} [Props.homesFilter] - Additional filters to apply to the home search.
 * @param {boolean} [Props.hideCreateNewOption] - Whether to hide the "Create New" option in the home search.
 *
 * @returns
 */
export const AddHomeDrawer = ({
  open,
  account,
  builderId,
  communityId,
  city,
  regionCode,
  readOnlyFields,
  onClose,
  onCreate,
  title = "Create New Home",
  showHomeSearch,
  onCreateWithSelectedHome,
  homesFilter,
  hideCreateNewOption,
  initialAddress,
  quickBooksId,
}) => {
  const { classes, theme } = useStyles({ showHomeSearch });

  const [selectedHome, setSelectedHome] = useState(null);

  const homeForm = useAddHomeForm({
    account,
    builderId,
    communityId,
    city,
    regionCode,
    readOnlyFields,
    onCreate,
    initialAddress,
    quickBooksId,
  });

  const handleClose = () => {
    homeForm.onResetForm();
    onClose();
  };

  const handleCreateHome = () => {
    if (onCreateWithSelectedHome && selectedHome) {
      onCreateWithSelectedHome(selectedHome);
      setSelectedHome(null);
    } else {
      homeForm.handleCreateHome();
    }
  };

  return (
    <MiContentDrawer
      variant="temporary"
      open={open}
      onClose={handleClose}
      title={title}
      leftIcon={mdi[ICONS_WEB.HOME]}
      rightIcon={mdi[ICONS_WEB.CLOSE]}
      actions={[
        {
          name: "Cancel",
          variant: BUTTON_VARIANTS.TEXT,
          size: "medium",
          color: BUTTON_COLORS.BLACK,
          onClick: handleClose,
        },
        {
          name: selectedHome ? "Link Home" : "Create",
          variant: BUTTON_VARIANTS.CONTAINED,
          size: "medium",
          color: BUTTON_COLORS.PRIMARY,
          onClick: handleCreateHome,
          disabled: homeForm.isSubmitDisabled && !selectedHome,
        },
      ]}
    >
      <Box className={classes.searchContainer}>
        {showHomeSearch && (
          <Box className={classes.container} mb={4} width="100%">
            <MiCard
              title="Search for Existing Home"
              leftIcon={mdi.mdiHomeOutline}
              containerStyles={{ padding: theme.spacing(2) }}
            >
              <HomeAndAccountSearch
                onSelect={setSelectedHome}
                onReset={() => setSelectedHome(null)}
                homeFilters={homesFilter}
                hideCreateNewOption={hideCreateNewOption}
              />
            </MiCard>
          </Box>
        )}
        {!selectedHome && (
          <Box className={classes.container} width="100%">
            <AddHomeForm {...homeForm} hideFooter />
          </Box>
        )}
      </Box>
    </MiContentDrawer>
  );
};

const useStyles = makeStyles()((theme, { showHomeSearch }) => ({
  container: {
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
  },
  searchContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    width: "100%",
    paddingTop: showHomeSearch ? 4 : 0,
  },
}));
